/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent, ProgressBarComponent } from '../../../../../core/components';
import { Component, ComponentRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { DownloadEnum } from '../../enums';
import { DownloadService, LogService, SocketService } from '../../../../../core/services';
import { HttpEventType } from '@angular/common/http';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-adc-setup',
    templateUrl: './adc-setup.component.html',
    styleUrls: ['./adc-setup.component.scss']
})

export class AdcSetupComponent extends BaseComponent implements OnInit {
    /**
     * Reference to new ADC container
     */
    @ViewChild('progressContainer', { read: ViewContainerRef })
    public progressContainer: any;

    /**
     * Toggle view for existing AdDC add.
     */
    @Input() public addExisting: boolean = false;

    /**
     * Only trigger the socket listener when in existing mode and this component is active.
     */
    @Input() set triggerSocketListener(state: boolean) {
        if (state && this.addExisting) {
            this.setupSocketListener();
        }
    }

    /**
     * Set the component to full width for a modal.
     */
    @Input() public inModal: boolean = false;

    /**
     * Center the title
     */
    @Input() public centerTitle: boolean = false;

    /**
     * Platform details.
     */
    @Input() public platform: any = {};

    /**
     * Emit progress of setup.
     */
    @Output() public setupStateEmitter: EventEmitter<any> = new EventEmitter();

    /**
     * Enum reference for the view.
     */
    public adoptionEnum = DownloadEnum;

    /**
     * Store content click.
     */
    public clickedItemTitle: string;

    /**
     * Set the progress state as the user selections options.
     */
    public progressState: DownloadEnum = DownloadEnum.WAITING;

    /**
     * File location for downloads.
     */
    public fileDownloadUrl = 'https://downloads.loadbalancer.org/releases/virtual/current/';

    /**
     * Store the selected simple download option names.
     */
    public simpleDownloadProgressState = [];

    /**
     * Modal Component Reference
     */
    public componentReference: ComponentRef<any>;

    /**
     * Store the ADC address that is going to be adopted.
     */
    public adcAddressForAdoption: string;

    constructor(
        private downloadService: DownloadService,
        private logService: LogService,
        private socketService: SocketService
    ) {
        super();
    }

    ngOnInit() {
        if (this.addExisting) {
            this.progressState = DownloadEnum.SETUP;
        }
    }

    /**
     * Helper function for displaying step 1.
     */
    public isStepOne(): boolean {
        return this.progressState === this.adoptionEnum.WAITING ||
            this.progressState === this.adoptionEnum.DOWNLOADING ||
            this.progressState === this.adoptionEnum.DOWNLOADED;
    }

    /**
     * Helper function for displaying step 2.
     */
    public isStepTwo(): boolean {
        return this.progressState === this.adoptionEnum.SETUP || this.progressState === this.adoptionEnum.SETUP_COMPLETE;
    }

    /**
     * Helper function for displaying step 3.
     */
    public isStepThree(): boolean {
        return this.progressState === this.adoptionEnum.CONNECTED;
    }

    /**
     * Move to setup step.
     * @param EnumValue
     */
    public nextStep(EnumValue: DownloadEnum) {
        this.setupSocketListener();
        this.progressState = EnumValue;
    }

    /**
     * Trigger the download in the download component.
     * @param service
     */
    public triggerDownload(service: { fileName: string; name: string; }) {
        if (this.progressState === DownloadEnum.DOWNLOADING) {
            this.logService.warn('AddLoadbalancerComponent: triggerDownload', 'Download already in progress.');
        } else if (service && !service.fileName) {
            this.logService.warn('AddLoadbalancerComponent: triggerDownload', 'No filename for selection.');
        } else {
            this.progressState = DownloadEnum.DOWNLOADING;
            this.clickedItemTitle = service.name;
            const fileUrl = this.fileDownloadUrl + service.fileName;
            const progressComponent = this.progressContainer.createComponent(ProgressBarComponent);
            // Set value in the progress component.
            progressComponent.instance.fileUrl = fileUrl;
            progressComponent.instance.spinner = true;
            progressComponent.instance.onCompleteCallBack = this.cleanUpProgressComponent.bind(this);
            // Component reference for destroy later.
            this.componentReference = progressComponent;
        }
    }

    /**
     * Clean up progress component.
     * @private
     */
    private cleanUpProgressComponent(): void {
        this.progressState = DownloadEnum.DOWNLOADED;
        this.setupSocketListener();
        if (this.componentReference) {
            this.componentReference.destroy();
        }
    }

    /**
     * Trigger a simple download.
     * @param service
     */
    public triggerSimpleDownload(service: { fileName: string; name: any; }): void {
        const fileUrl = this.fileDownloadUrl + service.fileName;
        this.simpleDownloadProgressState.push(service.name);
        this.downloadService.download(fileUrl, 'blob')
            .pipe(takeUntil(this.destroyed$))
            .subscribe(result => {
                    if (result.type === HttpEventType.Response) {
                        this.downloadService.generateDownload(result.body, fileUrl);
                    }
                },
                error => {
                    this.logService.error('AddLoadbalancerComponent: triggerSimpleDownload', error);
                });
    }

    /**
     * Setup socket listeners.
     * @private
     */
    private setupSocketListener(): void {
        this.socketService.on('AdcAdd')
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                data => {
                    this.adcAddressForAdoption = data.data && data.data.ipAddress;
                    this.progressState = this.adoptionEnum.SETUP_COMPLETE;
                }
            );
    }
}
